import React, { useState } from 'react';

import { Switch, Route, Redirect, RouteProps } from 'react-router-dom';

import { css } from '@emotion/css';
import { RecurrencyRole, TenantProductName, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';

import { EditDetailsPage } from 'pages/account/EditDetailsPage';
import { MyMcpSessionsPage } from 'pages/account/MyMcpSessionsPage';
import { CentralizedAuditLogsPage } from 'pages/admin/CentralizedAuditLogsPage/CentralizedAuditLogsPage';
import { ExportJobDetailsPage } from 'pages/admin/ExportJobsPage/ExportJobDetailsPage';
import { ExportJobsPage } from 'pages/admin/ExportJobsPage/ExportJobsPage';
import { IntegrationStatusPage } from 'pages/admin/IntegrationStatusPage/IntegrationStatusPage';
import { LegacySalesSettingsPage } from 'pages/admin/LegacySalesSettingsPage';
import { LocationSettingsPage } from 'pages/admin/LocationSettingsPage';
import { recurrencyRolesToShowSettingsPage, SettingsPage } from 'pages/admin/SettingsPage';
import { SyncStatusPage } from 'pages/admin/SyncStatusPage/SyncStatusPage';
import { TenantMcpSessionsPage } from 'pages/admin/TenantMcpSessionsPage';
import { UpdateCredentialsPage } from 'pages/admin/UpdateCredentialsPage';
import { UsersPage } from 'pages/admin/UsersPage';
import { RuleDetailPage } from 'pages/admin/ValidationRules/RuleDetailPage';
import { ValidationRulesPage } from 'pages/admin/ValidationRules/ValidationRules';
import { ValidationRulesTemplatesPage } from 'pages/admin/ValidationRules/ValidationRuleTemplates.tsx';
import { hasChatWithDataAccess } from 'pages/chat/access';
import { ChatWithDataPage } from 'pages/chat/ChatWithDataPage';
import { DailyWorkflowScreen } from 'pages/dailyWorkflow/DailyWorkflowScreen';
import { TenantDetailsPage } from 'pages/internal/tenants/TenantDetailsPage';
import { TenantEditPage } from 'pages/internal/tenants/TenantEditPage';
import { TenantListPage } from 'pages/internal/tenants/TenantListPage';
import { TenantNewPage } from 'pages/internal/tenants/TenantNewPage';
import { UserDetailsPage } from 'pages/internal/users/UserDetailsPage';
import { UserEditPage } from 'pages/internal/users/UserEditPage';
import { UserListPage } from 'pages/internal/users/UserListPage';
import { UserNewPage } from 'pages/internal/users/UserNewPage';
import { ItemDetailsPage } from 'pages/items/ItemDetailsPage';
import { NoAccessPage } from 'pages/NoAccessPage';
import { NotFoundPage } from 'pages/NotFoundPage';
import { OrderDetailsPage } from 'pages/orders/orders/OrderDetailsPage';
import { OrderListPage } from 'pages/orders/orders/OrderListPage';
import { OrderNewPage } from 'pages/orders/orders/OrderNewPage';
import { DraftQuoteListPage } from 'pages/orders/quotes/DraftQuoteListPage';
import { QuoteDetailsPage } from 'pages/orders/quotes/QuoteDetailsPage';
import { QuoteEditPage } from 'pages/orders/quotes/QuoteEditPage';
import { QuoteListPage } from 'pages/orders/quotes/QuoteListPage';
import { QuoteNewPage } from 'pages/orders/quotes/QuoteNewPage';
import { DisputeListPage } from 'pages/payments/disputes';
import { OpenInvoicesPage } from 'pages/payments/OpenInvoicesPage';
import { OrderPayPage } from 'pages/payments/OrderPayPage';
import { PaymentsListPage } from 'pages/payments/PaymentsListPage';
import { PayoutListPage } from 'pages/payments/payouts';
import { PayoutDetailsPage } from 'pages/payments/payouts/PayoutDetailsPage';
import { PlanningWorkflowPage } from 'pages/planning/PlanningWorkflow/PlanningWorkflowPage';
import { ReplenishmentPathsPage } from 'pages/planning/ReplenishmentPathsPage/ReplenishmentPathsPage';
import { ForecastingPage } from 'pages/purchasing/ForecastingPage/ForecastingPage';
import { PlanningPage } from 'pages/purchasing/PlanningPage/PlanningPage';
import { PurchaseGroupsPage } from 'pages/purchasing/PurchaseGroupsPage/PurchaseGroupsPage';
import { PurchaseOrderDetailsPage } from 'pages/purchasing/purchaseOrders/PurchaseOrderDetailsPage';
import { PurchaseOrderListPage } from 'pages/purchasing/purchaseOrders/PurchaseOrderListPage';
import { PurchaseOrderNewPage } from 'pages/purchasing/purchaseOrders/PurchaseOrderNewPage';
import { PurchaseStatusPage } from 'pages/purchasing/PurchaseStatsPage/PurchaseStatusPage';
import { PurchaseTargetsPage } from 'pages/purchasing/PurchaseTargetsPage';
import { PurchaseTargetLinesPage } from 'pages/purchasing/PurchaseTargetsPage/PurchaseTargetLinesPage';
import { InventoryMissionControlPage } from 'pages/purchasing/PurchasingDashboardPage';
import {
  CurrentInventoryExplorerPage,
  HistoricalInventoryExplorerPage,
} from 'pages/purchasing/PurchasingDashboardPage/InventoryExplorer';
import { PurchasingItemListPage } from 'pages/purchasing/PurchasingItemListPage';
import { TransferOrderDetailsPage } from 'pages/purchasing/TransferOrders/TransferOrderDetailsPage';
import { TransferOrderListPage } from 'pages/purchasing/TransferOrders/TransferOrderListPage';
import { TransferReplenishmentLinesPage } from 'pages/purchasing/TransferReplenishmentPage/TransferReplenishmentLinesPage/TransferReplenishmentLinesPage';
import { TransferReplenishmentPage } from 'pages/purchasing/TransferReplenishmentPage/TransferReplenishmentPage';
import { VendorDetailsPage } from 'pages/purchasing/vendors/VendorDetailsPage';
import { VendorListPage } from 'pages/purchasing/vendors/VendorListPage';
import { ExplorerPage } from 'pages/reporting/ExplorerPage';
import { ReportPage } from 'pages/reporting/ReportPage';
import { SavedReportListPage } from 'pages/reporting/SavedReportListPage';
import { CustomerDetailsPage } from 'pages/sales/customers/CustomerDetailsPage';
import { CustomerListPage } from 'pages/sales/customers/CustomerListPage';
import { ProspectNewPage } from 'pages/sales/customers/ProspectNewPage/ProspectNewPage';
import { DashboardPage } from 'pages/sales/DashboardPage';
import { PricingPage } from 'pages/sales/PricingPage';
import { SalesItemListPage } from 'pages/sales/SalesItemListPage';
import { Integration } from 'pages/setup/Integration';
import { TaskDetailsPage } from 'pages/tasks/TaskDetailsPage';
import { TasksListPage } from 'pages/tasks/TasksListPage';

import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { Container } from 'components/Container';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { getStoredHomePage } from 'utils/homePage';
import {
  arePermissionsEnabled,
  hasUserPermission,
  isAtLeastOneOfRecurrencyRoles,
  shouldShowProduct,
} from 'utils/roleAndTenant';
import { makePath, routePaths } from 'utils/routes';
import { track, TrackEvent } from 'utils/track';

import { navModules, filterNav } from './Navigation/navModulesData';

export interface ProductRouteProps extends RouteProps {
  products: TenantProductName[];
  permission?: TenantUserPermission;
}

export interface RecurrencyRoleRouteProps extends RouteProps {
  recurrencyRoles: RecurrencyRole[];
  products?: TenantProductName[];
  permission?: TenantUserPermission;
}

const ProductRouteBanner = ({
  message,
  onClick,
  buttonText,
}: {
  message: string;
  onClick: () => void;
  buttonText: string;
}) => (
  <Container>
    <Alert
      className={css`
        margin-top: 24px;
      `}
      message={message}
      action={
        <Button onClick={onClick} size="small">
          {buttonText}
        </Button>
      }
      type="warning"
      banner
      closable
    />
  </Container>
);

const ProductRoute = (props: ProductRouteProps) => {
  const { activeTenant, activeUser } = useGlobalApp();
  const [showInternalPreview, setShowInternalPreview] = useState(false);
  const { isRecurrencyInternalUser } = activeUser;
  const shouldShowRoute =
    props.products.some((product) => shouldShowProduct(activeTenant, product)) &&
    (!props.permission || hasUserPermission(activeTenant, props.permission));

  if (shouldShowRoute || showInternalPreview) {
    return (
      <>
        {!shouldShowRoute && (
          <ProductRouteBanner
            message={`Showing internal preview of this page. ${
              activeTenant.name
            } users cannot access this page as ${props.products.join(' or ')} is not enabled.`}
            onClick={() => setShowInternalPreview(!showInternalPreview)}
            buttonText="See Regular User's View"
          />
        )}
        <Route {...props} />
      </>
    );
  }

  if (!isRecurrencyInternalUser) {
    track(TrackEvent.Nav_NoAccess, { blockedProducts: props.products });
  }

  return (
    <>
      {isRecurrencyInternalUser && (
        <ProductRouteBanner
          message={`Showing regular user's view of this page. ${
            activeTenant.name
          } users cannot access this page as ${props.products.join(' or ')} is not enabled.`}
          onClick={() => setShowInternalPreview(!showInternalPreview)}
          buttonText="Preview Page"
        />
      )}
      <Route children={<NoAccessPage />} />
    </>
  );
};

const AdminRoute = (props: RouteProps) => {
  const { activeUser } = useGlobalApp();
  const isRecurrencyAdminUser = activeUser.isRecurrencyAdmin;

  if (isRecurrencyAdminUser) {
    return <Route {...props} />;
  }
  return <Route children={<NoAccessPage />} />;
};

const ChatWithDataRoute = (props: RouteProps) => {
  const { activeTenant, activeUser } = useGlobalApp();

  if (hasChatWithDataAccess(activeTenant, activeUser)) {
    return <Route {...props} />;
  }
  return <Route children={<NoAccessPage />} />;
};

const RecurrencyRoleRoute = ({ recurrencyRoles, products, permission, ...props }: RecurrencyRoleRouteProps) => {
  const { activeTenant } = useGlobalApp();
  // once a tenant is on permissions, the permission replaces the role; role-only routes keep their role gate
  const hasRole =
    (permission !== undefined && arePermissionsEnabled()) ||
    isAtLeastOneOfRecurrencyRoles(activeTenant, recurrencyRoles);
  const hasProduct = products === undefined || products.some((product) => shouldShowProduct(activeTenant, product));
  const hasPermission = permission === undefined || hasUserPermission(activeTenant, permission);

  if (hasRole && hasProduct && hasPermission) {
    return <Route {...props} />;
  }
  return <Route children={<NoAccessPage />} />;
};

export function AppRoutes() {
  const { activeTenant, activeUser, activeErpRole } = useGlobalApp();

  const homeModule = filterNav(navModules, activeTenant, activeUser, activeErpRole)[0];
  if (!homeModule) {
    return <Route children={<NoAccessPage />} />;
  }
  const homeRoute = homeModule.routeFn?.() || homeModule.subModules[0].routeFn();
  const userHomePage = getStoredHomePage(activeTenant.tenantUser.settings?.homePage);

  return (
    <>
      {/* prettier-ignore */}
      <Switch>
        <Route exact path={routePaths.home} children={<Redirect to={userHomePage ?? makePath(homeRoute)} />} />

        <ProductRoute exact products={[TenantProductName.SalesMissionControl]} permission={TenantUserPermission.AccessSalesDashboardView} path={routePaths.sales.dashboard} component={DashboardPage} />

        <ProductRoute exact products={[TenantProductName.SalesReporting]} permission={TenantUserPermission.AccessReportingReportingView} path={routePaths.reporting.explorer} component={ExplorerPage} />
        <ProductRoute exact products={[TenantProductName.SalesReporting]} permission={TenantUserPermission.AccessReportingReportingView} path={routePaths.reporting.report} component={ReportPage} />
        <ProductRoute exact products={[TenantProductName.SalesReporting]} permission={TenantUserPermission.AccessReportingReportingView} path={routePaths.reporting.reports} component={SavedReportListPage} />

        <ProductRoute exact products={[TenantProductName.SalesSearchAndLookup]} path={routePaths.sales.pricing} component={PricingPage} />
        <ProductRoute exact products={[TenantProductName.SalesSearchAndLookup]} permission={TenantUserPermission.AccessSalesCustomersView} path={routePaths.sales.customerList} component={CustomerListPage} />
        <ProductRoute exact products={[TenantProductName.SalesSearchAndLookup]} permission={TenantUserPermission.AccessSalesCustomersView} path={routePaths.sales.customerDetails} component={CustomerDetailsPage} />
        <ProductRoute exact products={[TenantProductName.SalesSearchAndLookup]} permission={TenantUserPermission.AccessPurchasingInventoryView} path={routePaths.sales.itemList} component={SalesItemListPage} />
        <ProductRoute exact products={[TenantProductName.SalesSearchAndLookup]} permission={TenantUserPermission.AccessPurchasingInventoryView} path={routePaths.sales.itemDetails} component={ItemDetailsPage} />
        <ProductRoute exact products={[TenantProductName.SalesQuoting]} permission={TenantUserPermission.AccessOrdersQuotesView} path={routePaths.orders.quoteList} component={QuoteListPage} />
        <ProductRoute exact products={[TenantProductName.SalesQuoting]} permission={TenantUserPermission.AccessOrdersQuotesView} path={routePaths.orders.quoteDetails} component={QuoteDetailsPage} />

        <ProductRoute exact products={[TenantProductName.SalesSearchAndLookup, TenantProductName.DfpaSearchAndLookup]} permission={TenantUserPermission.AccessOrdersOrdersView} path={routePaths.orders.orderList} component={OrderListPage} />
        <ProductRoute exact products={[TenantProductName.SalesSearchAndLookup, TenantProductName.DfpaSearchAndLookup]} permission={TenantUserPermission.AccessOrdersOrdersView} path={routePaths.orders.orderDetails} component={OrderDetailsPage} />

        <ProductRoute exact products={[TenantProductName.SalesCsrFrontCounter]} permission={TenantUserPermission.AccessOrdersOrdersView} path={routePaths.orders.orderNew} component={OrderNewPage} />

        <ProductRoute exact products={[TenantProductName.SalesQuoting]} permission={TenantUserPermission.AccessOrdersQuotesView} path={routePaths.orders.draftQuoteList} component={DraftQuoteListPage} />
        <ProductRoute exact products={[TenantProductName.SalesQuoting]} permission={TenantUserPermission.AccessOrdersQuotesView} path={routePaths.orders.quoteNew} component={QuoteNewPage} />
        <ProductRoute exact products={[TenantProductName.SalesQuoting]} permission={TenantUserPermission.AccessOrdersQuotesView} path={routePaths.orders.quoteEdit} component={QuoteEditPage} />

        <ProductRoute exact products={[TenantProductName.DfpaMissionControl]} permission={TenantUserPermission.AccessPlanningDashboardView} path={routePaths.demandPlanning.dashboard} component={InventoryMissionControlPage} />
        <ProductRoute exact products={[TenantProductName.DfpaMissionControl]} permission={TenantUserPermission.AccessPlanningDashboardView} path={routePaths.demandPlanning.reporting} component={HistoricalInventoryExplorerPage} />
        <ProductRoute exact products={[TenantProductName.DfpaMissionControl]} permission={TenantUserPermission.AccessPlanningDashboardView} path={routePaths.demandPlanning.currentInventory} component={CurrentInventoryExplorerPage} />

        <ProductRoute exact products={[TenantProductName.DfpaDemandPlanning]} permission={TenantUserPermission.AccessPlanningForecastingView} path={routePaths.demandPlanning.forecasting} component={ForecastingPage} />
        <ProductRoute exact products={[TenantProductName.DfpaDemandPlanning]} permission={TenantUserPermission.AccessPlanningPlanningView} path={routePaths.demandPlanning.planning} component={PlanningPage} />
        <ProductRoute exact products={[TenantProductName.DfpaDemandPlanning]} permission={TenantUserPermission.AccessPlanningDailyWorkflowView} path={routePaths.demandPlanning.dailyWorkflow} component={DailyWorkflowScreen} />
        <ProductRoute exact products={[TenantProductName.DfpaDemandPlanning]} permission={TenantUserPermission.AccessPlanningPlanningView} path={routePaths.demandPlanning.planningWorkflow} component={PlanningWorkflowPage} />

        <ProductRoute exact products={[TenantProductName.DfpaSearchAndLookup]} permission={TenantUserPermission.AccessPurchasingPurchaseOrdersView} path={routePaths.purchasing.purchaseOrderList} component={PurchaseOrderListPage} />
        <ProductRoute exact products={[TenantProductName.DfpaSearchAndLookup]} permission={TenantUserPermission.AccessPurchasingPurchaseOrdersView} path={routePaths.purchasing.purchaseOrderDetails} component={PurchaseOrderDetailsPage} />
        <ProductRoute exact products={[TenantProductName.DfpaSearchAndLookup]} permission={TenantUserPermission.AccessPurchasingVendorsView} path={routePaths.purchasing.vendorList} component={VendorListPage} />
        <ProductRoute exact products={[TenantProductName.DfpaSearchAndLookup]} permission={TenantUserPermission.AccessPurchasingVendorsView} path={routePaths.purchasing.vendorDetails} component={VendorDetailsPage} />
        <ProductRoute exact products={[TenantProductName.DfpaSearchAndLookup]} permission={TenantUserPermission.AccessPurchasingInventoryView} path={routePaths.purchasing.itemList} component={PurchasingItemListPage} />
        <ProductRoute exact products={[TenantProductName.DfpaSearchAndLookup]} permission={TenantUserPermission.AccessPurchasingInventoryView} path={routePaths.purchasing.itemDetails} component={ItemDetailsPage} />

        <ProductRoute exact products={[TenantProductName.DfpaPurchasingAutomation]} path={routePaths.purchasing.purchaseOrderNew} component={PurchaseOrderNewPage} />
        <ProductRoute exact products={[TenantProductName.DfpaPurchasingAutomation]} permission={TenantUserPermission.AccessPurchasingPurchaseTargetsView} path={routePaths.purchasing.purchaseTargets} component={PurchaseTargetsPage} />
        <ProductRoute exact products={[TenantProductName.DfpaPurchasingAutomation]} permission={TenantUserPermission.AccessPurchasingPurchaseTargetsView} path={routePaths.purchasing.purchaseTargetLines} component={PurchaseTargetLinesPage} />
        <ProductRoute exact products={[TenantProductName.DfpaPurchasingAutomation]} permission={TenantUserPermission.AccessPurchasingPurchaseGroupsView} path={routePaths.purchasing.purchaseGroups} component={PurchaseGroupsPage} />
        <ProductRoute exact products={[TenantProductName.DfpaPurchasingAutomation]} permission={TenantUserPermission.AccessPurchasingPurchaseStatusView} path={routePaths.purchasing.purchaseStatus} component={PurchaseStatusPage} />
        <ProductRoute exact products={[TenantProductName.DfpaPurchasingAutomation]} permission={TenantUserPermission.AccessPurchasingTransferReplenishmentView} path={routePaths.purchasing.transferReplenishment} component={TransferReplenishmentPage} />
        <ProductRoute exact products={[TenantProductName.DfpaPurchasingAutomation]} permission={TenantUserPermission.AccessPurchasingTransferReplenishmentView} path={routePaths.purchasing.transferReplenishmentLines} component={TransferReplenishmentLinesPage} />
        <ProductRoute exact products={[TenantProductName.DfpaPurchasingAutomation]} permission={TenantUserPermission.AccessPurchasingTransferOrdersView} path={routePaths.purchasing.transferOrderList} component={TransferOrderListPage} />
        <ProductRoute exact products={[TenantProductName.DfpaPurchasingAutomation]} permission={TenantUserPermission.AccessPurchasingTransferOrdersView} path={routePaths.purchasing.transferOrderDetails} component={TransferOrderDetailsPage} />

        <ProductRoute exact products={[TenantProductName.Payments]} path={routePaths.payments.payouts} component={PayoutListPage} />
        <ProductRoute exact products={[TenantProductName.Payments]} path={routePaths.payments.disputes} component={DisputeListPage} />
        <ProductRoute exact products={[TenantProductName.Payments]} path={routePaths.payments.payoutDetails} component={PayoutDetailsPage} />
        <ProductRoute exact products={[TenantProductName.Payments]} path={routePaths.payments.payments} component={PaymentsListPage} />
        <ProductRoute exact products={[TenantProductName.Payments]} path={routePaths.payments.openInvoices} component={OpenInvoicesPage} />
        <ProductRoute exact products={[TenantProductName.Payments]} path={routePaths.payments.paymentDetails} component={OrderPayPage} />

        <ProductRoute exact products={[TenantProductName.SalesCrm]} permission={TenantUserPermission.AccessTasksTasksView} path={routePaths.tasks.taskList} component={TasksListPage} />
        <ProductRoute exact products={[TenantProductName.SalesCrm]} permission={TenantUserPermission.AccessTasksTasksView} path={routePaths.tasks.taskDetails} component={TaskDetailsPage} />
        <ProductRoute exact products={[TenantProductName.SalesQuoting, TenantProductName.SalesCsrFrontCounter]} permission={TenantUserPermission.AccessSalesCustomersView} path={routePaths.sales.customerNew} component={ProspectNewPage} />

        <AdminRoute exact path={routePaths.internal.userList} component={UserListPage} />
        <AdminRoute exact path={routePaths.internal.userDetails} component={UserDetailsPage} />
        <AdminRoute exact path={routePaths.internal.userNew} component={UserNewPage} />
        <AdminRoute exact path={routePaths.internal.userEdit} component={UserEditPage} />
        <AdminRoute exact path={routePaths.internal.tenantList} component={TenantListPage} />
        <AdminRoute exact path={routePaths.internal.tenantDetails} component={TenantDetailsPage} />
        <AdminRoute exact path={routePaths.internal.tenantEdit} component={TenantEditPage} />
        <AdminRoute exact path={routePaths.internal.tenantNew} component={TenantNewPage} />

        <RecurrencyRoleRoute exact recurrencyRoles={[RecurrencyRole.TenantAdmin]} permission={TenantUserPermission.AccessRuleBuilderRulesView} path={routePaths.ruleBuilder.ruleManagement} component={ValidationRulesPage} />
        <AdminRoute exact path={routePaths.ruleBuilder.ruleTemplates} component={ValidationRulesTemplatesPage} />
        <RecurrencyRoleRoute exact recurrencyRoles={[RecurrencyRole.TenantAdmin]} permission={TenantUserPermission.AccessRuleBuilderRulesView} path={routePaths.ruleBuilder.ruleDetails} component={RuleDetailPage} />

        <AdminRoute exact path={routePaths.settings.legacySalesSettings} component={LegacySalesSettingsPage} />
        <RecurrencyRoleRoute exact recurrencyRoles={recurrencyRolesToShowSettingsPage} permission={TenantUserPermission.AccessSettingsGeneralView} path={routePaths.settings.settings} component={SettingsPage} />
        <RecurrencyRoleRoute exact recurrencyRoles={[RecurrencyRole.TenantAdmin]} permission={TenantUserPermission.AccessSettingsUsersView} path={routePaths.settings.users} component={UsersPage} />
        <RecurrencyRoleRoute exact recurrencyRoles={[RecurrencyRole.TenantAdmin]} permission={TenantUserPermission.AccessSettingsGeneralView} path={routePaths.settings.replenishmentPaths} component={ReplenishmentPathsPage} />
        <RecurrencyRoleRoute exact recurrencyRoles={[RecurrencyRole.TenantAdmin]} permission={TenantUserPermission.AccessSettingsGeneralView} path={routePaths.settings.locationSettings} component={LocationSettingsPage} />
        <RecurrencyRoleRoute exact recurrencyRoles={[RecurrencyRole.TenantAdmin]} permission={TenantUserPermission.AccessSettingsGeneralView} path={routePaths.settings.updateCredentials} component={UpdateCredentialsPage} />
        <RecurrencyRoleRoute exact recurrencyRoles={[RecurrencyRole.TenantAdmin]} permission={TenantUserPermission.AccessSettingsGeneralView} path={routePaths.settings.integration} component={IntegrationStatusPage} />

        <RecurrencyRoleRoute exact recurrencyRoles={[RecurrencyRole.TenantAdmin]} permission={TenantUserPermission.AccessSettingsMonitoringView} path={routePaths.monitoring.centralizedAuditLogs} component={CentralizedAuditLogsPage} />
        <RecurrencyRoleRoute exact recurrencyRoles={[RecurrencyRole.TenantAdmin]} permission={TenantUserPermission.AccessSettingsMonitoringView} path={routePaths.monitoring.exportJobs} component={ExportJobsPage} />
        <RecurrencyRoleRoute exact recurrencyRoles={[RecurrencyRole.TenantAdmin]} permission={TenantUserPermission.AccessSettingsMonitoringView} path={routePaths.monitoring.exportJobDetails} component={ExportJobDetailsPage} />
        <RecurrencyRoleRoute exact recurrencyRoles={[RecurrencyRole.TenantAdmin]} permission={TenantUserPermission.AccessSettingsMonitoringView} path={routePaths.monitoring.syncStatus} component={SyncStatusPage} />
        <RecurrencyRoleRoute
          exact
          recurrencyRoles={[RecurrencyRole.TenantAdmin]}
          products={[TenantProductName.Mcp]}
          permission={TenantUserPermission.AccessSettingsMcpSessionsView}
          path={routePaths.monitoring.mcpSessions}
          component={TenantMcpSessionsPage}
        />

        <Route exact path={routePaths.account.accountEdit} component={EditDetailsPage} />
        {/* a stored MCP write implies read, so this admits either MCP permission */}
        <ProductRoute exact products={[TenantProductName.Mcp]} permission={TenantUserPermission.ActionMcpRead} path={routePaths.account.mcpSessions} component={MyMcpSessionsPage} />
        <Route exact path={routePaths.setup.integration} component={Integration} />

        <ChatWithDataRoute exact path={routePaths.chat.chatWithData} component={ChatWithDataPage} />

        <Route component={NotFoundPage} />
      </Switch>
    </>
  );
}
